/**
 * Icons.tsx — 設計 v2 §7 圖示（24×24 線條；`stroke = currentColor`；筆劃 1.6；圓頭）
 *
 * 來源：`.coappery/tasks/J-ui-cosingery/design/icons.jsx`（**逐字**移植；UI v2 步 4）。
 * ⚠️ 一律 `aria-hidden` —— 意思由按鈕 `aria-label`／旁邊文字承擔（設計 §8）；**唔准 emoji**。
 */
import type { ReactNode } from 'react';

function makeIcon(paths: ReactNode) {
  return function Icon({ size = '1.5rem' }: { size?: string }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {paths}
      </svg>
    );
  };
}

export const Icons = {
  /* 導航 */
  More: makeIcon(
    <>
      <circle cx="5" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="19" cy="12" r="1.3" fill="currentColor" />
    </>,
  ),
  Close: makeIcon(<path d="M6 6l12 12M18 6L6 18" />),
  Back: makeIcon(<path d="M15 5l-7 7 7 7" />),
  ChevronDown: makeIcon(<path d="M6 9l6 6 6-6" />),
  ChevronL: makeIcon(<path d="M14 6l-6 6 6 6" />),
  ChevronR: makeIcon(<path d="M10 6l6 6-6 6" />),
  /* 對話 */
  Send: makeIcon(<path d="M12 19V6M6.5 11.5L12 6l5.5 5.5" />),
  NewChat: makeIcon(<path d="M12 5v14M5 12h14" />),
  Pen: makeIcon(
    <>
      <path d="M15.5 4.5l4 4L9 19l-4.5 1 1-4.5z" />
      <path d="M13.5 6.5l4 4" />
    </>,
  ),
  /* 播放 */
  Play: makeIcon(<path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />),
  Pause: makeIcon(
    <>
      <rect x="7" y="5.5" width="3.2" height="13" rx="1" fill="currentColor" />
      <rect x="13.8" y="5.5" width="3.2" height="13" rx="1" fill="currentColor" />
    </>,
  ),
  Prev: makeIcon(
    <>
      <path d="M17.5 6v12L9 12z" fill="currentColor" />
      <path d="M6.5 6v12" />
    </>,
  ),
  Next: makeIcon(
    <>
      <path d="M6.5 6v12L15 12z" fill="currentColor" />
      <path d="M17.5 6v12" />
    </>,
  ),
  Shuffle: makeIcon(
    <>
      <path d="M4 7h3.5c2 0 3 1 4.5 5s2.5 5 4.5 5H20" />
      <path d="M4 17h3.5c1.3 0 2.2-.4 3-1.6M13.5 8.6c.8-1.2 1.7-1.6 3-1.6H20" />
      <path d="M17.5 4.5L20 7l-2.5 2.5M17.5 14.5L20 17l-2.5 2.5" />
    </>,
  ),
  Repeat: makeIcon(
    <>
      <path d="M5 11V9.5A2.5 2.5 0 0 1 7.5 7H19" />
      <path d="M16.5 4.5L19 7l-2.5 2.5" />
      <path d="M19 13v1.5a2.5 2.5 0 0 1-2.5 2.5H5" />
      <path d="M7.5 19.5L5 17l2.5-2.5" />
    </>,
  ),
  RepeatOne: makeIcon(
    <>
      <path d="M5 11V9.5A2.5 2.5 0 0 1 7.5 7H19" />
      <path d="M16.5 4.5L19 7l-2.5 2.5" />
      <path d="M19 13v1.5a2.5 2.5 0 0 1-2.5 2.5H5" />
      <path d="M7.5 19.5L5 17l2.5-2.5" />
      <path d="M11.3 10.6l1.2-.8v4.6" strokeWidth={1.4} />
    </>,
  ),
  Heart: makeIcon(<path d="M12 19s-7-4.3-7-9.5A3.8 3.8 0 0 1 12 7.3 3.8 3.8 0 0 1 19 9.5C19 14.7 12 19 12 19z" />),
  HeartFill: makeIcon(
    <path d="M12 19s-7-4.3-7-9.5A3.8 3.8 0 0 1 12 7.3 3.8 3.8 0 0 1 19 9.5C19 14.7 12 19 12 19z" fill="currentColor" />,
  ),
  Volume: makeIcon(
    <>
      <path d="M5 10v4h3l4 3.5v-11L8 10z" />
      <path d="M15.5 9.5a3.5 3.5 0 0 1 0 5" />
    </>,
  ),
  PlaylistAdd: makeIcon(
    <>
      <path d="M4 7h11M4 12h11M4 17h7" />
      <path d="M18 14v6M15 17h6" />
    </>,
  ),
  Share: makeIcon(
    <>
      <path d="M12 4v11" />
      <path d="M8 7.5L12 4l4 3.5" />
      <path d="M6 12v6.5A1.5 1.5 0 0 0 7.5 20h9a1.5 1.5 0 0 0 1.5-1.5V12" />
    </>,
  ),
  Style: makeIcon(
    <>
      <path d="M5 7h14M5 12h14M5 17h14" />
      <circle cx="9" cy="7" r="1.8" fill="var(--color-card)" />
      <circle cx="15" cy="12" r="1.8" fill="var(--color-card)" />
      <circle cx="8" cy="17" r="1.8" fill="var(--color-card)" />
    </>,
  ),
  Check: makeIcon(<path d="M5.5 12.5l4 4 9-9" />),
  /* 抽屜 */
  Diary: makeIcon(
    <>
      <path d="M6 4.5h11a1.5 1.5 0 0 1 1.5 1.5v12.5a1.5 1.5 0 0 1-1.5 1.5H6z" />
      <path d="M6 4.5v15M9 4.5v15" />
      <path d="M12 9h4M12 12h4" />
    </>,
  ),
  Playlist: makeIcon(
    <>
      <path d="M4 7h11M4 12h11M4 17h8" />
      <circle cx="17.5" cy="17" r="2" />
      <path d="M19.5 17V9.5l1.5-.5" />
    </>,
  ),
  Settings: makeIcon(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4L18 18M6 18l1.6-1.6M16.4 7.6L18 6" />
    </>,
  ),
  Feedback: makeIcon(
    <>
      <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4 3.5V16a2 2 0 0 1-2-2z" />
      <path d="M9 9h6M9 12h3.5" />
    </>,
  ),
  Account: makeIcon(
    <>
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5.5 19.5c1-3.3 3.5-5 6.5-5s5.5 1.7 6.5 5" />
    </>,
  ),
  Export: makeIcon(
    <>
      <path d="M12 4v11" />
      <path d="M8 11l4 4 4-4" />
      <path d="M5 18.5h14" />
    </>,
  ),
  /* ⭐ 2026-10-11（`decisions/053`）：底部 tab bar「傾偈」（對話泡泡） */
  Chat: makeIcon(
    <>
      <path d="M4.5 6.5A2.5 2.5 0 0 1 7 4h10a2.5 2.5 0 0 1 2.5 2.5v6A2.5 2.5 0 0 1 17 15H10l-4.5 4v-4H7a2.5 2.5 0 0 1-2.5-2.5z" />
    </>,
  ),
};

export type IconName = keyof typeof Icons;
export default Icons;
